<template>
  <div>
    <SynchronizingModal
      v-if="isSynchronizing"
      :isSynchronizing="isSynchronizing"
    />

    <v-row
      no-gutters
    >
      <v-col
        cols="12"
        class="hero__container"
        :style="`height: ${heroHeight}px;`"
      >
        <v-img
          loading
          :src="heroImage"
          :height="heroHeight"
        />
      </v-col>
    </v-row>

    <v-container>
      <v-row>
        <v-col cols="12" align="right">
          <v-alert
            v-if="mailSyncSuccess"
            dense
            text
            type="success"
          >
            {{ mailSyncSuccess }}
          </v-alert>

          <v-alert
            v-if="mailSyncError"
            dense
            outlined
            type="error"
          >
            {{ mailSyncError }}
          </v-alert>

          <v-btn
            v-if="isOnline && awaitingSends.length > 0"
            outlined
            :loading="isSynchronizingMails"
            class="rounded-0"
            height="47"
            @click="sendAwaitingDemands"
          >
            {{ $tc('buttons.sync-send', awaitingSends.length, { n: awaitingSends.length }) }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col cols="12">

          <h2>
            {{ $t('advices.selection-url') }}<br>
          </h2>

          <div
            v-if="hasFavorites"
            v-clipboard:copy="sharingUrl"
            v-clipboard:success="copyLink"
            class="sharing-url__container"
          >
            <v-text-field
              :value="sharingUrl"
              :append-icon="icons.copy"
              readonly
            />
          </div>
          <div
            v-else
            class="no-favorites"
          >{{ $t('advices.no-selected-favorites') }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <AdviceForm
            v-if="!isFetching"
            :is-sending="isSending"
            @sendForm="sendForm"
          />

          <AdviceFormSkeleton
            v-else
          />

          <v-alert
            v-if="hasBeenSuccessful"
            dense
            text
            type="success"
          >
            {{ $t('advices.messages.success') }}
          </v-alert>

          <v-alert
            v-if="isSavedForLater"
            dense
            text
            type="success"
          >
            {{ $t('advices.messages.saved') }}
          </v-alert>

          <v-alert
            v-if="error"
            dense
            outlined
            type="error"
          >
            {{ $t(error) }}
          </v-alert>

          <v-alert
            v-if="warning"
            dense
            outlined
            type="warning"
          >
            {{ $t(warning) }}
          </v-alert>
        </v-col>
      </v-row>
    </v-container>

    <v-snackbar
      tile
      v-model="hasBeenCopied"
      timeout="6000"
    >
      <div class="sharing-snackbar__title">
        <strong>{{ $t('messages.has-been-copied') }}</strong>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
  import Vue from 'vue'
  import VueClipboard from 'vue-clipboard2'
  import { mdiContentCopy } from '@mdi/js'

  import { deepClone } from '../utils'

  import { localstorageStore } from '../plugins/localforage'

  Vue.use(VueClipboard)

  const AdviceForm = () => import(/* webpackPrefetch: true */'../components/advices/AdviceForm.vue')
  const SynchronizingModal = () => import(/* webpackPrefetch: true */'../components/search/SynchronizingModal.vue')

  import AdviceFormSkeleton from '../components/advices/AdviceFormSkeleton.vue'

  export default {
    name: 'AdvicePage',

    props: {
      isFetching: {
        required: true,
        type: Boolean,
      },

      isSynchronizing: {
        required: true,
        type: Boolean,
      },
    },

    components: {
      AdviceForm,
      SynchronizingModal,
      AdviceFormSkeleton,
    },

    data() {
      return {
        icons: {
          copy: mdiContentCopy,
        },
        isSending: false,
        hasBeenCopied: false,
        hasBeenSuccessful: false,
        isSavedForLater: false,
        mailSyncError: '',
        mailSyncSuccess: '',
        isSynchronizingMails: false,
        awaitingSends: [],
      }
    },

    computed: {
      locale() {
        return this.$store.state.locale
      },

      error() {
        return this.$store.state.error.mails
      },

      warning() {
        return this.$store.state.warning.mails
      },

      isOnline() {
        return this.$store.state.isOnline
      },

      favoritesIds() {
        return this.$store.state.objects.favorites
      },

      favorites() {
        const pois = this.$store.state.objects.pois.filter((poi) => this.favoritesIds.includes(poi.id))

        return pois
      },

      hasFavorites() {
        return this.favoritesIds.length > 0
      },

      sharingUrl() {
        return this.$store.getters.sharingUrl
      },

      heroHeight() {
        return this.$vuetify.breakpoint.name === 'xs'
          ? 200
          : 450
      },

      heroImage() {
        return require('@/assets/images/team.jpg')
      },
    },

    methods: {
      copyLink() {
        this.hasBeenCopied = true
      },

      async sendForm(form) {

        this.isSending = true

        const { sharingUrl } = this

        delete form.isValid

        const { dispatch, commit } = this.$store

        const { locale } = this
        const data = { ...form, locale }
        if (sharingUrl) data.sharingUrl = sharingUrl

        if (!this.isOnline) {
          await this.saveForLater(data)

          this.isSending = false
          this.isSavedForLater = true

          await commit('clearMailsError')
          await commit('clearMailsWarning')

          return
        }

        const response = await dispatch('sendForm', data)
        if (!response || !response.mailSent) return

        const confirmation = await dispatch('sendConfirmation', data)
        if (!confirmation || !confirmation.mailSent) return

        this.isSending = false
        this.hasBeenSuccessful = true

        await commit('clearMailsError')
        await commit('clearMailsWarning')
      },

      async saveForLater(data) {
        this.awaitingSends.push(data)

        await localstorageStore.setItem('awaitingSends', JSON.stringify(this.awaitingSends))
      },

      async sendAwaitingDemands() {
        this.isSynchronizingMails = true
        this.mailSyncError = ''
        this.mailSyncSuccess = ''

        const { dispatch } = this.$store

        const mailToSendNumber = this.awaitingSends.length
        let mailSendNumber = 0

        const sendStack = deepClone(this.awaitingSends)

        while (sendStack.length > 0 && !this.mailSyncError) {
          const mail = sendStack.pop()

          try {
            const response = await dispatch('sendForm', mail)
            if (!response || !response.mailSent) throw new Error()

            const confirmation = await dispatch('sendConfirmation', mail)
            if (!confirmation || !confirmation.mailSent) throw new Error()
          } catch (err) {
            sendStack.push(mail)
            this.awaitingSends = sendStack
            await localstorageStore.setItem('awaitingSends', JSON.stringify(this.awaitingSends))

            mailSendNumber = mailToSendNumber - this.awaitingSends.length

            this.mailSyncError = this.$tc('errors.mail-sync', mailSendNumber, {
              n: mailSendNumber,
              m: mailToSendNumber,
            })

            this.isSynchronizingMails = false
          }
        }

        if (this.mailSyncError) return

        this.awaitingSends = []
        await localstorageStore.setItem('awaitingSends', '[]')

        this.mailSyncSuccess = this.$tc('advices.messages.synced', mailToSendNumber, { n: mailToSendNumber })
        this.isSynchronizingMails = false
      },
    },

    async mounted() {
      document.title = 'Venue Finder - Lausanne Tourisme'
      const awaitingSends = JSON.parse(await localstorageStore.getItem('awaitingSends'))

      if (awaitingSends) this.awaitingSends = awaitingSends
    },
  }
</script>

<style lang="sass" scoped>
  @import "@/assets/styles/_config.sass"
  @import "@/assets/styles/_mixins.sass"

  h3
    overflow-wrap: break-word

    .no-favorites
      margin-top: 15px

    .sharing-url__container,
    .sharing-url__container ::v-deep input,
    .sharing-url__container ::v-deep .v-input__slot
      cursor: pointer !important

  .v-btn
    @include basic-btn
    margin-top: 10px
</style>
